import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

import type {
  IDefaultFilter,
  ITicketListResult,
  IAddCommentParams,
  ICommentListResult,
  IGetTicketListResponse,
  IGetCommentListResponse,
} from '../tickets.types';

// -------------------------Ticket API Endpoints-------------------------
const URL_ROUTES = {
  list: 'ticket/list',
  add: 'ticket/add',
  update: 'ticket/update',
  details: 'ticket/details',
  delete: 'ticket/delete',
  listComment: 'ticket/commentList',
  addComment: 'ticket/addComment',
  export: 'ticket/export',
};
// ----------------------------------------------------------------------

const commonQueryParams = {
  headers: { 'Content-Type': 'application/json' },
  method: 'POST',
};

export const ticketApi = createApi({
  reducerPath: 'ticketApi',
  baseQuery,
  endpoints: (builder) => ({
    // Get Ticket List
    getTicketList: builder.mutation<ITicketListResult, IDefaultFilter>({
      query: (filterQuery) => ({
        ...commonQueryParams,
        url: URL_ROUTES.list,
        body: filterQuery,
      }),
      transformResponse: (response: IGetTicketListResponse): ITicketListResult => ({
        list: response?.data?.ticket?.list?.map((listItem) => ({ ...listItem })) ?? [],
        total: response?.data?.ticket?.total ?? 0,
      }),
    }),
    // Create Ticket Record
    createTicket: builder.mutation({
      query: (params) => ({
        ...commonQueryParams,
        url: URL_ROUTES.add,
        body: params,
      }),
    }),
    // Update Ticket Record
    updateTicket: builder.mutation({
      query: ({ id, updates }) => ({
        ...commonQueryParams,
        url: URL_ROUTES.update,
        body: { id, ...updates },
      }),
    }),
    // Delete Ticket Record
    deleteTickets: builder.mutation({
      query: (ids: string[]) => ({
        ...commonQueryParams,
        url: URL_ROUTES.delete,
        body: { ids },
      }),
    }),
    // Get Ticket Details
    getTicketDetails: builder.mutation({
      query: (id: string) => ({
        ...commonQueryParams,
        url: URL_ROUTES.details,
        body: { id },
      }),
    }),
    // Comment List
    getCommentList: builder.mutation<ICommentListResult, IDefaultFilter>({
      query: (filterQuery) => ({
        ...commonQueryParams,
        url: URL_ROUTES.listComment,
        body: filterQuery,
      }),
      transformResponse: (response: IGetCommentListResponse) => ({
        list:
          response?.data?.comment?.list?.map((listItem) => ({
            id: listItem.id ?? '',
            userName: `${listItem.user?.firstName ?? ''} ${listItem?.user?.lastName ?? ''}`,
            ProfileURL: listItem?.user?.ProfileURL ?? '',
            content: listItem?.content ?? '',
            createdAt: listItem?.createdAt ?? '',
            attachments: listItem?.attachment ?? [],
          })) ?? [],
        total: response?.data?.comment?.total ?? 0,
      }),
    }),
    // Add Comment
    addComment: builder.mutation({
      query: (params: IAddCommentParams) => ({
        ...commonQueryParams,
        url: URL_ROUTES.addComment,
        body: params,
      }),
    }),
    // EXPORT
    export: builder.mutation({
      query: (params: any) => ({
        ...commonQueryParams,
        url: URL_ROUTES.export,
        body: params,
      }),
    }),
  }),
});

export const {
  useGetTicketListMutation,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useGetTicketDetailsMutation,
  useDeleteTicketsMutation,
  // Comment
  useGetCommentListMutation,
  useAddCommentMutation,
  useExportMutation,
} = ticketApi;
